import { createContext, useContext, useMemo, useState } from "react";
import { countries } from "../../../data/countries";
import { countryCodes } from "../../../data/countryCodes";

/**
 * Signup form state shared by the signup steps (design: state/signup.tsx).
 * Lives in memory only, like the design; the password is never written to storage.
 * The design's sample clinic (used to seed its canvas) is left out on purpose.
 */

/** Where the auth screens live. They match the app's existing routes so redirects keep working. */
export const AUTH_PATHS = {
  signin: "/login",
  signup: "/register",
  forgot: "/forgot-password",
  welcome: "/welcome",
  dashboard: "/dashboard",
};

export const emptySignup = {
  name: "",
  email: "",
  phone: "",
  password: "",
  agree: false,
  /** where the clinic is; India keeps GST / PAN / 6-digit PIN, anywhere else a tax ID and free-form address */
  country: "India",
  /** the mobile number's dialling code */
  dialCode: "+91",
  taxId: "",
  orgName: "",
  orgType: "",
  address: "",
  city: "",
  state: "",
  pin: "",
  website: "",
  hasGst: true,
  gstin: "",
  legalName: "",
  pan: "",
  billingSame: true,
  verified: false,
  /** the email a code was last sent to; the backend refuses a second send-otp for it */
  otpSentTo: "",
};

const SignupContext = createContext(null);

export function SignupProvider({ children }) {
  const [data, setData] = useState(emptySignup);
  const value = useMemo(
    () => ({
      data,
      set: (patch) => setData((d) => ({ ...d, ...patch })),
      reset: () => setData(emptySignup),
    }),
    [data],
  );
  return <SignupContext.Provider value={value}>{children}</SignupContext.Provider>;
}

export function useSignup() {
  const ctx = useContext(SignupContext);
  if (!ctx) throw new Error("useSignup must be inside SignupProvider");
  return ctx;
}

export const steps = [
  { id: "basic", label: "Your details", path: `${AUTH_PATHS.signup}/basic` },
  { id: "organisation", label: "Organisation", path: `${AUTH_PATHS.signup}/organisation` },
  { id: "tax", label: "Tax details", path: `${AUTH_PATHS.signup}/tax` },
  { id: "verify", label: "Verify", path: `${AUTH_PATHS.signup}/verify` },
  { id: "setup", label: "Account setup", path: `${AUTH_PATHS.signup}/setup` },
];
export const stepPath = (id) => steps.find((s) => s.id === id).path;

export const orgTypes = [
  "Single-doctor clinic",
  "Multi-speciality clinic",
  "Dental clinic",
  "Hospital",
  "Diagnostic centre",
  "Physiotherapy centre",
  "Skin and hair clinic",
  "Other",
];

export const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
  "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

/** GST state codes, first two digits of a GSTIN. */
export const gstStateCodes = {
  "Jammu and Kashmir": "01", "Himachal Pradesh": "02", Punjab: "03", Chandigarh: "04", Uttarakhand: "05",
  Haryana: "06", Delhi: "07", Rajasthan: "08", "Uttar Pradesh": "09", Bihar: "10", Sikkim: "11",
  "Arunachal Pradesh": "12", Nagaland: "13", Manipur: "14", Mizoram: "15", Tripura: "16", Meghalaya: "17",
  Assam: "18", "West Bengal": "19", Jharkhand: "20", Odisha: "21", Chhattisgarh: "22", "Madhya Pradesh": "23",
  Gujarat: "24", "Dadra and Nagar Haveli and Daman and Diu": "26", Maharashtra: "27", Karnataka: "29", Goa: "30",
  Lakshadweep: "31", Kerala: "32", "Tamil Nadu": "33", Puducherry: "34", "Andaman and Nicobar Islands": "35",
  Telangana: "36", "Andhra Pradesh": "37", Ladakh: "38",
};

export const COUNTRY_OPTIONS = countries.map((c) => ({ value: c, label: c }));
/** Each dialling code once, labelled with the countries that use it (so searching "Emirates" finds +971); the label truncates after the code. */
export const DIAL_OPTIONS = [...new Set(countryCodes.map((c) => c.code))].map((code) => ({ value: code, label: `${code} ${countryCodes.filter((c) => c.code === code).map((c) => c.country).join(", ")}` }));
/** The dialling code of a country, when the list has it. */
export const dialCodeOf = (country) => countryCodes.find((c) => c.country === country)?.code || "";
export const isIndia = (data) => (data.country || "India") === "India";

export const firstName = (name) => String(name || "").replace(/^dr\.?\s*/i, "").trim().split(/\s+/)[0] ?? "";
